import React, { useState } from 'react'

import { EuiFlyoutHeader, EuiFlyoutBody, EuiFlyoutFooter, EuiTitle, EuiButton, EuiFlexGroup, EuiFlexItem, EuiFormRow, EuiFieldText, EuiTextArea, EuiSpacer } from '@elastic/eui'
import AcFlyout from 'components/AcFlyout'

import { GeographicCluster } from 'api/interfaces/MarketAnalysis'
import BlueprintEditWarning from 'features/planner/components/BlueprintEditWarning'

interface EditGeographicClusterFlyoutProps {
  mode: 'add' | 'edit'
  cluster?: GeographicCluster
  onSave: (cluster: GeographicCluster) => void
  onClose: () => void
  isUpdating: boolean
  showBlueprintWarning?: boolean
}

const emptyCluster: GeographicCluster = {
  clusterName: '',
  alignmentScore: '',
  priorityLevel: '',
  keyAreas: '',
  strategicRationale: ''
}

const EditGeographicClusterFlyout: React.FC<EditGeographicClusterFlyoutProps> = ({ mode, cluster, onSave, onClose, isUpdating, showBlueprintWarning = false }) => {
  const [formData, setFormData] = useState<GeographicCluster>(cluster ?? emptyCluster)

  const handleSave = () => {
    onSave(formData)
  }

  const isSaveDisabled = !formData.clusterName.trim()

  return (
    <AcFlyout onClose={onClose} size="l">
      <EuiFlyoutHeader>
        <EuiTitle size="m">
          <h2>{mode === 'add' ? 'Add Geographic Cluster' : `Edit ${formData.clusterName}`}</h2>
        </EuiTitle>
      </EuiFlyoutHeader>
      <EuiFlyoutBody>
        <BlueprintEditWarning show={showBlueprintWarning} />
        <EuiFormRow label="Cluster Name" fullWidth>
          <EuiFieldText
            fullWidth
            value={formData.clusterName}
            onChange={e => setFormData(prev => ({ ...prev, clusterName: e.target.value }))}
          />
        </EuiFormRow>
        <EuiSpacer size="m" />
        <EuiFormRow label="Alignment Score" fullWidth>
          <EuiFieldText
            fullWidth
            value={formData.alignmentScore}
            onChange={e => setFormData(prev => ({ ...prev, alignmentScore: e.target.value }))}
          />
        </EuiFormRow>
        <EuiSpacer size="m" />
        <EuiFormRow label="Priority Level" fullWidth>
          <EuiFieldText
            fullWidth
            value={formData.priorityLevel}
            onChange={e => setFormData(prev => ({ ...prev, priorityLevel: e.target.value }))}
          />
        </EuiFormRow>
        <EuiSpacer size="m" />
        <EuiFormRow label="Key Areas" fullWidth>
          <EuiTextArea
            fullWidth
            value={formData.keyAreas}
            onChange={e => setFormData(prev => ({ ...prev, keyAreas: e.target.value }))}
            rows={4}
          />
        </EuiFormRow>
        <EuiSpacer size="m" />
        <EuiFormRow label="Strategic Rationale" fullWidth>
          <EuiTextArea
            fullWidth
            value={formData.strategicRationale}
            onChange={e => setFormData(prev => ({ ...prev, strategicRationale: e.target.value }))}
            rows={4}
          />
        </EuiFormRow>
      </EuiFlyoutBody>
      <EuiFlyoutFooter>
        <EuiFlexGroup direction="row" alignItems="center">
          <EuiFlexItem grow={false}>
            <EuiButton onClick={onClose} isDisabled={isUpdating}>
              Cancel
            </EuiButton>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiButton fill onClick={handleSave} isLoading={isUpdating} isDisabled={isSaveDisabled}>
              Save
            </EuiButton>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlyoutFooter>
    </AcFlyout>
  )
}

export default EditGeographicClusterFlyout
